/**
 * Ingredient-line parser: "2 cloves garlic, minced" → structured line.
 * Pure and dependency-free; the numeric half lives in units.ts.
 */
import { parseQuantity, parseUnit } from '../units.js';
const WORD_NUMBERS = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    half: 0.5,
    dozen: 12,
};
const QUALIFIER_RE = /^(?:just over|just under|about|approximately|approx\.?|roughly|around|scant|heaping|heaped|generous|nearly|almost|a good)\b\s*/i;
const TAIL_QUALIFIER_RE = /\b(?:or less|or more|or so|or to taste|plus more(?: for [a-z ]+)?|more to taste|as needed|if needed|if desired)\b/gi;
const SIZE_ADJ_RE = /^(?:extra[- ]large|jumbo|large|medium|small|mini)\b\s*/i;
const CONTAINER_RE = /^(cans?|jars?|bags?|boxes|box|bottles?|containers?|cartons?|tubs?|packets?|pkgs?|packages?)\b\.?\s*/i;
const SIZE_CONTAINER_RE = /^(?<size>\d+(?:[./]\d+)?(?:\s+\d+\/\d+)?)\s*-?\s*(?<sunit>oz|ounces?|g|grams?|ml|lbs?|pounds?|fl\.? ?oz)\.?\s+(?<cont>cans?|jars?|bags?|boxes|box|bottles?|containers?|cartons?|tubs?|packets?|pkgs?|packages?)\b\.?\s*/i;
const UNIT_CONTAINER_RE = /^(?<sunit>oz|ounces?|g|grams?|ml|lbs?|pounds?|fl\.? ?oz)\.?\s+(?<cont>cans?|jars?|bags?|boxes|box|bottles?|containers?|cartons?|tubs?|packets?|pkgs?|packages?)\b\.?\s*/i;
const PAREN_SIZE_RE = /^\s*(?:about\s+)?(?<size>\d+(?:[./]\d+)?(?:\s+\d+\/\d+)?)\s*-?\s*(?<sunit>oz|ounces?|g|grams?|ml|lbs?|pounds?|fl\.? ?oz|inch|in|cm)\b\.?(?:\s+(?:cans?|jars?|bags?|boxes|box|bottles?|containers?|packages?|pkgs?|each))?\s*$/i;
const PREP_WORDS = '(?:finely |roughly |thinly |thickly |coarsely |freshly |lightly )?(?:minced|chopped|diced|sliced|grated|shredded|crushed|peeled|melted|softened|beaten|cubed|julienned|sifted|toasted|mashed|halved|quartered|trimmed|divided|drained|rinsed|thawed|deveined|seeded|pitted|zested|juiced|crumbled|torn|cut into [^,]+|at room temperature|room temperature|for garnish|for serving)';
const TRAILING_PREP_RE = new RegExp(`^(?<name>.+?)\\s+(?<prep>${PREP_WORDS}(?:\\s.*)?)$`, 'i');
// Same vocabulary as PREP_WORDS, anchored to the start of a comma/semicolon/dash-delimited
// segment: used to decide whether that segment is real prep (a participle phrase, "cut into
// …", "for serving", "divided", …) rather than a bare descriptor ("boneless", "fresh",
// "large", …) that merely continues the ingredient name.
const PREP_SEGMENT_START_RE = new RegExp(`^${PREP_WORDS}`, 'i');
const LEADING_PREP_RE = /^(?:(?:finely |roughly |thinly |coarsely )?(?:minced|chopped|sliced|grated|peeled|melted|softened|beaten|sifted|cubed|julienned|mashed|toasted))\b\s*/i;
const PLURAL_EXCEPTIONS = new Set([
    'hummus',
    'couscous',
    'asparagus',
    'chives',
    'molasses',
    'grits',
    'oats',
    'greens',
    'swiss',
    'lentils',
    'brussels',
    'octopus',
    'gas',
    'bass',
    'citrus',
    'watercress',
]);
const IRREGULAR = {
    leaves: 'leaf',
    halves: 'half',
    loaves: 'loaf',
    tomatoes: 'tomato',
    potatoes: 'potato',
    chilies: 'chili',
    chillies: 'chilli',
    chiles: 'chile',
    cookies: 'cookie',
    pies: 'pie',
    fries: 'fry',
    anchovies: 'anchovy',
    cherries: 'cherry',
    berries: 'berry',
    strawberries: 'strawberry',
    blueberries: 'blueberry',
    raspberries: 'raspberry',
    cranberries: 'cranberry',
    radishes: 'radish',
    peaches: 'peach',
    bunches: 'bunch',
    feet: 'foot',
    knives: 'knife',
};
/** Singularise the last word of a name (basic English rules with an exception list). */
export function singularizeName(name) {
    const words = name.split(' ');
    const last = words[words.length - 1];
    if (PLURAL_EXCEPTIONS.has(last) || last.length < 4)
        return name;
    let out = IRREGULAR[last];
    if (out === undefined) {
        if (last.endsWith('ies'))
            out = last.slice(0, -3) + 'y';
        else if (/(ches|shes|sses|xes|zes)$/.test(last))
            out = last.slice(0, -2);
        else if (last.endsWith('ss') || last.endsWith('us') || !last.endsWith('s'))
            out = last;
        else
            out = last.slice(0, -1);
    }
    words[words.length - 1] = out;
    return words.join(' ');
}
function cleanName(text) {
    return text
        .toLowerCase()
        .replace(/[‘’]/g, "'")
        .replace(/^(?:of|the|an?)\s+/, '')
        .replace(/[\s,;:.*-]+$/, '')
        .replace(/^[\s,;:.*-]+/, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function pushPrep(parts, value) {
    const v = value.replace(/^[\s,;:-]+|[\s,;:.]+$/g, '').toLowerCase();
    if (v)
        parts.push(v);
}
const NAME_PREP_DELIM_RE = /^(?<seg>[^,;]+?)\s*(?:,|;|\s-\s)\s*(?<tail>.+)$/;
/**
 * Split "name, prep" (or "name; prep" / "name - prep") without mis-splitting
 * adjective chains like "boneless, skinless chicken thighs": a comma only
 * starts the prep clause once the text after it is actually a preparation
 * phrase (a participle, "cut into …", "divided", "for serving", …, per
 * `PREP_SEGMENT_START_RE`/`PREP_WORDS`). Segments that aren't recognised as
 * prep are folded back into the name instead of being discarded, so
 * "2 boneless, skinless chicken breasts, cut into 1-inch pieces" keeps
 * "boneless skinless chicken breasts" as the name and only pulls out
 * "cut into 1-inch pieces" as prep.
 */
function splitNameAndPrep(text) {
    const nameParts = [];
    let rest = text;
    for (;;) {
        const m = NAME_PREP_DELIM_RE.exec(rest);
        if (!m?.groups)
            break;
        const seg = m.groups['seg'];
        const tail = m.groups['tail'];
        if (PREP_SEGMENT_START_RE.test(tail.trim())) {
            nameParts.push(seg.trim());
            return { name: nameParts.join(' ').trim(), prep: tail };
        }
        nameParts.push(seg.trim());
        rest = tail;
    }
    nameParts.push(rest.trim());
    return { name: nameParts.join(' ').trim(), prep: null };
}
function containerUnit(word) {
    if (/^cans?$/i.test(word))
        return 'can';
    if (/^jars?$/i.test(word))
        return 'jar';
    if (/^bags?$/i.test(word))
        return 'bag';
    if (/^box(?:es)?$/i.test(word))
        return 'box';
    if (/^containers?$/i.test(word))
        return 'container';
    return 'package';
}
/** Parse one ingredient line as written in a recipe. Never throws. */
export function parseIngredientLine(raw) {
    const notes = [];
    const prepParts = [];
    let optional = false;
    let unit = null;
    let qty = null;
    let qtyMax;
    // 1. normalise, strip bullets / list numbering / footnote marks.
    // (No NFKC: it rewrites "1½" as "11⁄2"; parseQuantity reads vulgar fractions itself.)
    let text = raw
        .replace(/[\u00a0\t]/g, ' ')
        .replace(/\u2044/g, '/')
        .replace(/^[\s\-*•·▢◻☐]+/, '')
        .replace(/^\d+[.)]\s+(?=[\d¼½¾⅓⅔⅛⅜⅝⅞])/, '')
        .replace(/\s+/g, ' ')
        .trim();
    // 2. optional flag
    if (/\boptional\b/i.test(text)) {
        optional = true;
        text = text
            // whitespace is already collapsed to single spaces, so `?` (not `*`) is safe and linear
            .replace(/\( ?optional ?\)|,? ?\boptional\b,?/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }
    // 3. parentheticals: package sizes become notes, anything else is prep
    text = text.replace(/\(([^()]*)\)/g, (_m, inner) => {
        const size = PAREN_SIZE_RE.exec(inner);
        if (size?.groups)
            notes.push(`size: ${inner.trim()}`);
        else
            pushPrep(prepParts, inner);
        return ' ';
    });
    text = text.replace(/\s+/g, ' ').trim();
    // 4. "to taste" / "as needed" style tails and qualifiers
    const TO_TASTE = /,?\s*\b(?:to taste|as needed|to season|for seasoning|for sprinkling)\b/gi;
    if (/\b(?:to taste|as needed|to season|for seasoning|for sprinkling)\b/i.test(text) &&
        !/\bor to taste\b/i.test(text)) {
        unit = 'to_taste';
        text = text.replace(TO_TASTE, ' ').replace(/\s+/g, ' ').trim();
    }
    text = text.replace(TAIL_QUALIFIER_RE, (m) => {
        notes.push(m.toLowerCase());
        return ' ';
    });
    text = text.replace(/\s+/g, ' ').replace(/\s+,/g, ',').trim();
    // 5. "juice of 1 lemon" / "zest of 2 limes"
    const juice = /^(juice|zest)\s+of\s+(?:an?\s+|the\s+)?(.*)$/i.exec(text);
    if (juice) {
        pushPrep(prepParts, `${juice[1].toLowerCase()} of`);
        text = juice[2];
        if (!/^[\d¼½¾⅓⅔⅛⅜⅝⅞]/.test(text))
            qty = 1;
    }
    // 6. "pinch of" / "dash of" / "sprinkle of"
    const pinch = /^(?:an?\s+)?(pinch|pinches|dash|dashes|sprinkle|sprinkles)\s+(?:of\s+)?(.*)$/i.exec(text);
    if (pinch) {
        const w = pinch[1].toLowerCase();
        unit = w.startsWith('pinch') ? 'pinch' : w.startsWith('dash') ? 'dash' : 'to_taste';
        text = pinch[2];
    }
    // 7. leading quantity (digits, fractions, ranges) or a number word
    const parsed = parseQuantity(text);
    if (parsed.qty !== null) {
        qty = parsed.qty;
        if (parsed.qtyMax !== undefined)
            qtyMax = parsed.qtyMax;
        text = parsed.rest.replace(/^[.)]\s*/, '');
    }
    else {
        const word = /^(?:an?\s+)?([a-z]+)\b\s*(.*)$/i.exec(text);
        const num = word ? WORD_NUMBERS[word[1].toLowerCase()] : undefined;
        if (word && num !== undefined) {
            qty = num;
            text = word[2];
        }
        else if (/^an?\s+(?!few\b|couple\b|little\b|bit\b|handful\b)/i.test(text)) {
            qty = 1;
            text = text.replace(/^an?\s+/i, '');
        }
    }
    const dozen = /^dozen\b\s*/i.exec(text);
    if (dozen && qty !== null) {
        qty *= 12;
        text = text.slice(dozen[0].length);
    }
    const qual = QUALIFIER_RE.exec(text);
    if (qual) {
        notes.push(qual[0].trim().toLowerCase());
        text = text.slice(qual[0].length);
    }
    // 8. "1-inch piece ginger" style sizes glued to the number
    const glued = /^-\s*(inch|inches|in|cm)\b\s*(piece|pieces|knob|chunk|cube|cubes)?\s*(?:of\s+)?(.*)$/i.exec(text);
    if (glued) {
        pushPrep(prepParts, `${qty}-${glued[1].toLowerCase()} ${glued[2] ?? 'piece'}`.toLowerCase());
        qty = 1;
        text = glued[3];
    }
    // size adjectives before the unit/container ("small can tomato paste")
    const preSize = SIZE_ADJ_RE.exec(text);
    if (preSize) {
        pushPrep(prepParts, preSize[0]);
        text = text.slice(preSize[0].length);
    }
    // 9. "1 14-oz can", "14 oz can" → container unit + size note
    const sized = SIZE_CONTAINER_RE.exec(text);
    const sizedNoQty = sized ? null : UNIT_CONTAINER_RE.exec(text);
    if (sized?.groups) {
        notes.push(`size: ${sized.groups['size']} ${sized.groups['sunit'].toLowerCase()}`);
        unit = containerUnit(sized.groups['cont']);
        text = text.slice(sized[0].length);
    }
    else if (sizedNoQty?.groups && qty !== null) {
        // the number we already took was the package size
        notes.push(`size: ${qty} ${sizedNoQty.groups['sunit'].toLowerCase()}`);
        unit = containerUnit(sizedNoQty.groups['cont']);
        qty = 1;
        text = text.slice(sizedNoQty[0].length);
    }
    else {
        // 10. explicit unit word(s): two-word units first ("fl oz", "fluid ounces")
        const two = /^(\S+\s+\S+?)[.,]?(?:\s+|$)/.exec(text);
        const twoUnit = two ? parseUnit(two[1]) : null;
        if (twoUnit && /fl|fluid/i.test(two[1])) {
            unit = twoUnit;
            text = text.slice(two[0].length);
        }
        else {
            const one = /^(\S+?)[.,]?(?:\s+|$)/.exec(text);
            const oneUnit = one ? parseUnit(one[1]) : null;
            if (oneUnit && !(oneUnit === 'count' && qty === null)) {
                unit = oneUnit;
                text = text.slice(one[0].length);
            }
            else {
                const cont = CONTAINER_RE.exec(text);
                if (cont) {
                    unit = containerUnit(cont[1]);
                    text = text.slice(cont[0].length);
                    if (qty === null)
                        qty = 1;
                }
            }
        }
    }
    text = text.replace(/^(?:of\s+)+/i, '').trim();
    if (qty === null && (unit === 'can' || unit === 'package'))
        qty = 1;
    // 11. size adjectives ("large", "medium") → prep
    const size = SIZE_ADJ_RE.exec(text);
    if (size) {
        pushPrep(prepParts, size[0]);
        text = text.slice(size[0].length);
    }
    // 12. prep: after the first comma / " - " / ";" that actually starts a
    // preparation phrase, or a trailing participle phrase. Leading
    // comma-separated adjectives ("boneless, skinless chicken thighs") fold
    // back into the name — see splitNameAndPrep.
    const { name: splitName, prep: splitPrepText } = splitNameAndPrep(text);
    let name = splitName;
    if (splitPrepText !== null)
        pushPrep(prepParts, splitPrepText);
    const trailing = TRAILING_PREP_RE.exec(name);
    if (trailing?.groups) {
        name = trailing.groups['name'];
        pushPrep(prepParts, trailing.groups['prep']);
    }
    const leading = LEADING_PREP_RE.exec(name);
    if (leading && name.slice(leading[0].length).trim().split(' ').length <= 2) {
        pushPrep(prepParts, leading[0]);
        name = name.slice(leading[0].length);
    }
    name = cleanName(name.replace(/\s+(?:and|&)\s+/g, ' and '));
    if (name)
        name = singularizeName(name);
    // 13. unit fallbacks
    if (unit === null) {
        const seasoning = /(?:^|\s)(?:salt|pepper|peppercorn)$/.test(name) &&
            !/\b(?:bell|chili|chile|jalape[nñ]o|red|green|yellow|orange|banana|poblano|cayenne) pepper$/.test(name);
        unit = qty === null && seasoning ? 'to_taste' : 'count';
    }
    if (name.includes(' and '))
        notes.push('may be several ingredients');
    // 14. confidence
    const words = name ? name.split(' ').length : 0;
    let confidence;
    if (!name || words > 8)
        confidence = 'low';
    else if (qty !== null && (unit !== 'count' || words <= 4))
        confidence = 'high';
    else if (qty === null && unit === 'count')
        confidence = words > 4 ? 'low' : 'medium';
    else
        confidence = 'medium';
    const result = {
        qty,
        unit,
        name,
        prep: prepParts.length ? prepParts.join('; ') : null,
        optional,
        raw,
        confidence,
        notes,
    };
    if (qtyMax !== undefined)
        result.qtyMax = qtyMax;
    return result;
}
//# sourceMappingURL=parse-line.js.map